import React, { useMemo } from "react";
import { useQuery } from "urql";
import { keyBy, sortBy } from "lodash";
import { Link, useParams } from "wouter";

import { exportToXlsx } from "../../../../components/src/common/exportToXlsx";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../components/src/react-table/features";

import { ACCOUNTING_RUN, ACCOUNTING_RUNS, RECONCILIATION, RECONCILIATION_NAV } from "../../../../graphql/src/accountingrun2/queries";
import { CURRENCY_EXPONENT } from "../../../../graphql/src/accountingrun2/currencyExponent";
import { JOURNAL_ENTRIES } from "../../../../graphql/src/journalentry2/queries";
import { DEFAULT_TACCOUNTS } from "../../../../graphql/src/taccountchart2/defaults";

import { formatDecimal } from "./formatDecimal";
import {
    AccountingCompanyTypeEnum,
    AccountingRunStatusEnum,
    AccountingBatchType,
    AccountingTransactionType,
    CurrencyEnum,
    InstrumentModelTypeEnum,
    InstrumentProductTypeEnum,
    NavTransactionType,
    ReportStatusEnum,
    TAccountGroup1Enum,
    TAccountGroup2Enum,
    TransactionItemPerformanceType,
    TransactionItemType,
    TransactionStatus
} from "../../types.generated";
import type {
    AccountingRun2,
    CashAccountLadder,
    FxValuation,
    JournalEntry2,
    Nav,
    TradingManagerColumn,
    TransactionItem
} from "../../types.generated";

// as the old financial statements, general portfolios and pension trusts have no NAV
const NO_NAV_COMPANY_TYPES: string[] = [AccountingCompanyTypeEnum.GeneralPortfolio, AccountingCompanyTypeEnum.SwedishPensionTrust];

// the transactions of the portfolio, as the old financial statements
const PORTFOLIO_STATUSES = [
    TransactionStatus.Confirmed,
    TransactionStatus.Instructed,
    TransactionStatus.Pending,
    TransactionStatus.Preliminary,
    TransactionStatus.Settled
];

// amounts have 4 decimals and quantities 9 in journal entries
const AMOUNT_SCALE = 4;
const QUANTITY_SCALE = 9;
// positions are shown with 2 decimals as in the old financial statements, NAV units and NAV per unit with 4
const QUANTITY_DECIMALS = 2;
const UNIT_DECIMALS = 4;

interface PositionRow {
    instrumentId: string;
    name: string;
    portfolioQuantity: number;
    navQuantity: number;
    accountingQuantity: number;
    quantityDifference: number;
    navQuantityDifference: number;
    portfolioValue: number;
    navValue: number;
    accountingValue: number;
    valueDifference: number;
    navValueDifference: number;
}

interface BankAccountRow {
    instrumentId: string;
    name: string;
    currency: string;
    portfolioLocalValue: number;
    accountingLocalValue: number;
    localValueDifference: number;
    portfolioValue: number;
    accountingValue: number;
    valueDifference: number;
}

interface SummaryRow {
    name: string;
    portfolioValue: number;
    accountingValue: number;
    difference: number;
}

interface NavRow {
    instrumentId: string;
    name: string;
    units: number;
    navPerUnit: number;
    shareOfNav: number;
    navValue: number;
    accountingValue: number;
    difference: number;
}

// exact decimal string, e.g. a Decimal128 amount or quantity, to bigint units of 10^-scale
const toUnits = (value: string | null | undefined, scale: number): bigint => {
    if (value === null || typeof value === "undefined" || value === "") return 0n;
    const negative = value.startsWith("-");
    const [integerPart, fractionPart = ""] = value.replace("-", "").split(".");
    const units = BigInt(integerPart + fractionPart.padEnd(scale, "0").slice(0, scale));
    return negative ? -units : units;
};

// fx rate from currency to accounting currency by the fx valuations named e.g. USDSEK, 1 for the accounting currency
const getFxRate = (currency: string, accountingCurrency: string, fxValuationsByName: Record<string, FxValuation>): number => {
    if (!currency || currency === accountingCurrency) return 1;
    if (fxValuationsByName[currency + accountingCurrency]) return fxValuationsByName[currency + accountingCurrency].price;
    if (fxValuationsByName[accountingCurrency + currency]) return 1 / fxValuationsByName[accountingCurrency + currency].price;
    return Number.NaN;
};

// a difference of at most 1 in the accounting currency is small, e.g. rounding of the portfolio and NAV values that are summed
// floats, and yellow. Quantities are exact, so any quantity difference is red
const SMALL_VALUE_DIFFERENCE = 1;

// a number column, a difference column is yellow when the difference is small and red when larger, at the decimals shown.
// smallDifference is null for a column that is not a difference
const numberColumn = <T,>(
    key: keyof T & string,
    header: string,
    decimals: number,
    smallDifference: number | null = null,
    size = 125
): ReactTableColumnDef<T> => {
    const columnHelper = createReactTableColumnHelper<T>();
    return columnHelper.accessor((row: T) => row[key] as number, {
        id: key,
        header,
        cell: (cellProps) => {
            const value = cellProps.getValue() as number;
            const text = Number.isFinite(value) ? formatDecimal(value.toFixed(decimals), decimals) : "No fx rate";
            let backgroundColor: string | undefined;
            let color: string | undefined;
            if (smallDifference !== null && (!Number.isFinite(value) || Number(value.toFixed(decimals)) !== 0)) {
                if (Number.isFinite(value) && Math.abs(value) <= smallDifference) {
                    backgroundColor = "#ffc107";
                } else {
                    backgroundColor = "#dc3545";
                    color = "white";
                }
            }
            return <div style={{ textAlign: "right", color, backgroundColor }}>{text}</div>;
        },
        size,
        enableColumnFilter: false
    });
};

export function AccountingReconciliationPage(): React.ReactElement {
    const { clientId, id } = useParams<{ clientId: string; id: string }>();
    const [{ fetching, error, data }] = useQuery<{ accountingRuns2: AccountingRun2[] }>({
        query: ACCOUNTING_RUN,
        variables: { id },
        requestPolicy: "network-only"
    });
    const run = data && data.accountingRuns2 && data.accountingRuns2.length > 0 ? data.accountingRuns2[0] : null;
    const [{ fetching: fetchingRuns, error: runsError, data: runsData }] = useQuery<{ accountingRuns2: AccountingRun2[] }>({
        query: ACCOUNTING_RUNS,
        variables: {
            filter: {
                clientIdIn: [clientId],
                statusIn: [AccountingRunStatusEnum.Pending, AccountingRunStatusEnum.Preliminary, AccountingRunStatusEnum.Confirmed]
            }
        },
        requestPolicy: "network-only"
    });
    // journal entries of all non deleted runs of the period
    const [{ fetching: fetchingJournalEntries, error: journalEntriesError, data: journalEntriesData }] = useQuery<{
        journalEntries2: JournalEntry2[];
    }>({
        query: JOURNAL_ENTRIES,
        variables: { clientId, accountingPeriod: run ? run.accountingPeriod : null },
        requestPolicy: "network-only",
        pause: !run
    });
    // the period starts after the last Confirmed run of the previous period, which ends 30 or 31 December. The first run of a client
    // opens at 30 December of the previous year
    let previousEndDate: string | null = null;
    let startDate: string | null = null;
    if (run && runsData && runsData.accountingRuns2) {
        const previousPeriod = (Number(run.accountingPeriod) - 1).toFixed(0);
        previousEndDate = previousPeriod + "-12-30";
        for (const previousRun of runsData.accountingRuns2) {
            if (
                previousRun.accountingPeriod === previousPeriod &&
                previousRun.status === AccountingRunStatusEnum.Confirmed &&
                previousRun.endDate > previousEndDate
            ) {
                previousEndDate = previousRun.endDate;
            }
        }
        startDate = new Date(Date.parse(previousEndDate + "T00:00:00Z") + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    }
    const [{ fetching: fetchingPortfolio, error: portfolioError, data: portfolioData }] = useQuery<{
        positions: TradingManagerColumn[];
        previousPositions: TradingManagerColumn[];
        transactionItems: TransactionItem[];
        cashLadder: CashAccountLadder[];
        fxValuations: FxValuation[];
    }>({
        query: RECONCILIATION,
        variables: {
            clientId,
            endDate: run ? run.endDate : null,
            previousEndDate,
            startDate,
            fxDate: run ? run.endDate : null,
            statusIn: PORTFOLIO_STATUSES
        },
        requestPolicy: "network-only",
        pause: !run || !previousEndDate
    });

    const companyType = (run && (run.client?.accountingCompanyType as string)) || AccountingCompanyTypeEnum.SwedishMutualFund;
    const isFund = !NO_NAV_COMPANY_TYPES.includes(companyType);
    const [{ fetching: fetchingNav, error: navError, data: navData }] = useQuery<{ navs: Nav[] }>({
        query: RECONCILIATION_NAV,
        variables: { clientId, endDate: run ? run.endDate : null },
        requestPolicy: "network-only",
        pause: !run || !isFund
    });

    // amounts in accounting currency are shown with its ISO 4217 decimals, SEK when not set as in upsertAccountingRun
    const accountingCurrency = (run && (run.client?.accountingCurrency as CurrencyEnum)) || CurrencyEnum.SEK;
    const accountingDecimals = CURRENCY_EXPONENT[accountingCurrency];

    const { summaryRows, positionRows, bankAccountRows, navRows, nav } = useMemo(() => {
        const summaryRows: SummaryRow[] = [];
        const positionRows: PositionRow[] = [];
        const bankAccountRows: BankAccountRow[] = [];
        const navRows: NavRow[] = [];
        if (
            !run ||
            !runsData ||
            !runsData.accountingRuns2 ||
            !journalEntriesData ||
            !journalEntriesData.journalEntries2 ||
            !portfolioData
        ) {
            return { summaryRows, positionRows, bankAccountRows, navRows, nav: null as Nav | null };
        }

        // the balances at the run are the journal entries of the runs of the period up to and including it
        const runIds = new Set(
            runsData.accountingRuns2
                .filter((periodRun) => periodRun.accountingPeriod === run.accountingPeriod && periodRun.number <= run.number)
                .map((periodRun) => periodRun._id)
        );
        const tAccounts = run.clientTAccountChart ? run.clientTAccountChart.tAccounts || [] : DEFAULT_TACCOUNTS;
        const tAccountsByNumber = keyBy(tAccounts, "number");

        // accounting position per instrument. The value is the initial cost, value change, accrued interest and forward cash on the
        // balance sheet, the settled part leaves out forward cash, i.e. cash with a value date after the end date
        const accountingByInstrumentId: Record<
            string,
            { name: string; currency: string; quantity: bigint; value: bigint; settledQuantity: bigint; settledValue: bigint }
        > = {};
        // equity and its parts as in the old financial statements: create redeems are the share capital of the T batch and the ingoing
        // equity the equity of the IB batch
        let equityUnits = 0n;
        let createRedeemUnits = 0n;
        let dividendsPaidUnits = 0n;
        let ingoingEquityUnits = 0n;
        let resultUnits = 0n;
        for (const journalEntry of journalEntriesData.journalEntries2) {
            if (!runIds.has(journalEntry.accountingRunId)) continue;
            for (const transaction of journalEntry.transactions || []) {
                const amount = toUnits(transaction.amount, AMOUNT_SCALE);
                const tAccount = tAccountsByNumber[transaction.tAccountNumber];
                if (tAccount?.group1 === TAccountGroup1Enum.Equity) {
                    equityUnits += amount;
                    if (tAccount.group2 === TAccountGroup2Enum.ShareCapital && journalEntry.batch === AccountingBatchType.T) {
                        createRedeemUnits += amount;
                    }
                    if (tAccount.group2 === TAccountGroup2Enum.DividendPaid) dividendsPaidUnits += amount;
                    if (journalEntry.batch === AccountingBatchType.IB) ingoingEquityUnits += amount;
                }
                if (tAccount?.group2 === TAccountGroup2Enum.ProfitLossCurrentYear) resultUnits += amount;
                const settled =
                    transaction.type === AccountingTransactionType.InitialCost ||
                    transaction.type === AccountingTransactionType.ValueChange ||
                    transaction.type === AccountingTransactionType.AccruedInterest;
                if (!settled && transaction.type !== AccountingTransactionType.ForwardCash) continue;
                const instrumentId = transaction.instrumentId;
                if (!accountingByInstrumentId[instrumentId]) {
                    accountingByInstrumentId[instrumentId] = {
                        name: transaction.instrument?.name || instrumentId,
                        currency: transaction.instrument?.currency || "",
                        quantity: 0n,
                        value: 0n,
                        settledQuantity: 0n,
                        settledValue: 0n
                    };
                }
                const accounting = accountingByInstrumentId[instrumentId];
                const quantity = toUnits(transaction.quantity, QUANTITY_SCALE);
                accounting.quantity += quantity;
                accounting.value += amount;
                if (settled) {
                    accounting.settledQuantity += quantity;
                    accounting.settledValue += amount;
                }
            }
        }

        // portfolio positions per instrument, summed over the external accounts
        const portfolioByInstrumentId: Record<
            string,
            { name: string; quantity: number; value: number; modelType: string; productType: string }
        > = {};
        let portfolioEquity = 0;
        for (const position of portfolioData.positions || []) {
            portfolioEquity += position.exposure || 0;
            if (!position.instrumentId) continue;
            if (!portfolioByInstrumentId[position.instrumentId]) {
                portfolioByInstrumentId[position.instrumentId] = {
                    name: position.name,
                    quantity: 0,
                    value: 0,
                    modelType: position.instrument?.modelType,
                    productType: position.instrument?.productType
                };
            }
            portfolioByInstrumentId[position.instrumentId].quantity += position.quantity || 0;
            portfolioByInstrumentId[position.instrumentId].value += position.exposure || 0;
        }

        // the cash account balances by value date, the last balance on or before the end date
        const fxValuationsByName = keyBy(portfolioData.fxValuations || [], "name");
        const cashByInstrumentId: Record<string, { name: string; currency: string; balance: number }> = {};
        for (const ladder of portfolioData.cashLadder || []) {
            const instrument = ladder.instrument;
            if (
                instrument?.modelType !== InstrumentModelTypeEnum.Balance ||
                instrument?.productType !== InstrumentProductTypeEnum.CashAccount
            ) {
                continue;
            }
            let balance = 0;
            for (let i = 0; i < ladder.dates.length; i++) {
                if (ladder.dates[i] <= run.endDate) balance = ladder.balances[i];
            }
            cashByInstrumentId[ladder.instrumentId] = { name: instrument.name, currency: instrument.currency, balance };
        }

        // NAV positions and, on the default cash accounts, the NAV transactions in their currency, transfers to transient cash
        // accounts left out as in the old financial statements
        const activeNav = (navData?.navs || []).find((n) => n.status === ReportStatusEnum.Active) || null;
        const navByInstrumentId: Record<string, { quantity: number; value: number }> = {};
        if (activeNav) {
            for (const position of activeNav.data.positions || []) {
                if (!navByInstrumentId[position.instrumentId]) navByInstrumentId[position.instrumentId] = { quantity: 0, value: 0 };
                navByInstrumentId[position.instrumentId].quantity += parseFloat(position.quantity);
                navByInstrumentId[position.instrumentId].value += parseFloat(position.value);
            }
            const defaultAccount = (activeNav.client?.accounts || []).find((account) => account.defaultAccount);
            const defaultCashInstrumentIds = new Set((defaultAccount?.defaultCashInstruments || []).map((cash) => cash.partyInstrumentId));
            for (const instrumentId of Object.keys(navByInstrumentId)) {
                if (!defaultCashInstrumentIds.has(instrumentId)) continue;
                const currency = accountingByInstrumentId[instrumentId]?.currency || cashByInstrumentId[instrumentId]?.currency;
                for (const transaction of activeNav.data.transactions || []) {
                    if (transaction.currency !== currency || transaction.transactionType === NavTransactionType.Transfer) continue;
                    navByInstrumentId[instrumentId].quantity += parseFloat(transaction.amount);
                    navByInstrumentId[instrumentId].value += parseFloat(transaction.accountingAmount);
                }
            }
        }

        // bank accounts, the settled accounting position against the balance by value date
        for (const [instrumentId, cash] of Object.entries(cashByInstrumentId)) {
            const accounting = accountingByInstrumentId[instrumentId];
            const accountingLocalValue = accounting ? Number(accounting.settledQuantity) / 10 ** QUANTITY_SCALE : 0;
            const accountingValue = accounting ? Number(accounting.settledValue) / 10 ** AMOUNT_SCALE : 0;
            const portfolioValue = cash.balance * getFxRate(cash.currency, accountingCurrency, fxValuationsByName);
            bankAccountRows.push({
                instrumentId,
                name: cash.name,
                currency: cash.currency,
                portfolioLocalValue: cash.balance,
                accountingLocalValue,
                localValueDifference: accountingLocalValue - cash.balance,
                portfolioValue,
                accountingValue,
                valueDifference: accountingValue - portfolioValue
            });
        }

        // positions, the whole accounting position by trade date against the portfolio. Margin accounts, balances that are not cash
        // accounts, are left out as in the old financial statements
        const instrumentIds = new Set([...Object.keys(accountingByInstrumentId), ...Object.keys(portfolioByInstrumentId)]);
        for (const instrumentId of instrumentIds) {
            const accounting = accountingByInstrumentId[instrumentId];
            const portfolio = portfolioByInstrumentId[instrumentId];
            if (
                portfolio &&
                portfolio.modelType === InstrumentModelTypeEnum.Balance &&
                portfolio.productType !== InstrumentProductTypeEnum.CashAccount
            ) {
                continue;
            }
            const accountingQuantity = accounting ? Number(accounting.quantity) / 10 ** QUANTITY_SCALE : 0;
            const portfolioQuantity = portfolio ? portfolio.quantity : 0;
            // not reported when both are closed
            if (accountingQuantity === 0 && Number(portfolioQuantity.toFixed(QUANTITY_DECIMALS)) === 0) continue;
            const accountingValue = accounting ? Number(accounting.value) / 10 ** AMOUNT_SCALE : 0;
            const portfolioValue = portfolio ? portfolio.value : 0;
            const navQuantity = navByInstrumentId[instrumentId]?.quantity || 0;
            const navValue = navByInstrumentId[instrumentId]?.value || 0;
            positionRows.push({
                instrumentId,
                name: portfolio?.name || accounting?.name || instrumentId,
                portfolioQuantity,
                navQuantity,
                accountingQuantity,
                quantityDifference: accountingQuantity - portfolioQuantity,
                navQuantityDifference: accountingQuantity - navQuantity,
                portfolioValue,
                navValue,
                accountingValue,
                valueDifference: accountingValue - portfolioValue,
                navValueDifference: accountingValue - navValue
            });
        }

        // equity of the portfolio, the positions at the end date and the end of the previous period and the create redeems and dividends
        // paid of the period, against the equity, credit and thus negative when booked. Portfolio items are not accounted for
        let portfolioIngoingEquity = 0;
        for (const position of portfolioData.previousPositions || []) portfolioIngoingEquity += position.exposure || 0;
        let portfolioCreateRedeem = 0;
        let portfolioDividendsPaid = 0;
        for (const transactionItem of portfolioData.transactionItems || []) {
            if (transactionItem.performanceType === TransactionItemPerformanceType.PortfolioItem) continue;
            const fxRate = typeof transactionItem.fxRate === "number" ? transactionItem.fxRate : Number.NaN;
            if (transactionItem.type === TransactionItemType.DividendPaid) {
                portfolioDividendsPaid -= transactionItem.amount * fxRate;
            } else {
                portfolioCreateRedeem -= transactionItem.amount * fxRate;
            }
        }
        const portfolioResult = portfolioEquity - portfolioCreateRedeem - portfolioDividendsPaid - portfolioIngoingEquity;
        for (const [name, portfolioValue, units] of [
            ["Equity", portfolioEquity, equityUnits],
            ["Create redeem", portfolioCreateRedeem, createRedeemUnits],
            ["Dividends paid", portfolioDividendsPaid, dividendsPaidUnits],
            ["Ingoing equity", portfolioIngoingEquity, ingoingEquityUnits],
            ["Result", portfolioResult, resultUnits]
        ] as [string, number, bigint][]) {
            const accountingValue = -Number(units) / 10 ** AMOUNT_SCALE;
            summaryRows.push({ name, portfolioValue, accountingValue, difference: accountingValue - portfolioValue });
        }

        // NAV per share class in accounting currency against the equity, credit and thus negative when booked
        if (activeNav) {
            const accountingEquity = -Number(equityUnits) / 10 ** AMOUNT_SCALE;
            let totalNav = 0;
            let totalShare = 0;
            for (const fundClass of sortBy(activeNav.data.classes || [], "name")) {
                const navValue = parseFloat(fundClass.navAfterAdjustmentsAfterOrders) * parseFloat(fundClass.fxRate);
                totalNav += navValue;
                totalShare += parseFloat(fundClass.weight);
                navRows.push({
                    instrumentId: fundClass.instrumentId,
                    name: fundClass.name,
                    units: parseFloat(fundClass.unitsAfterOrders),
                    navPerUnit: parseFloat(fundClass.navPerUnit),
                    shareOfNav: parseFloat(fundClass.weight) * 100,
                    navValue,
                    accountingValue: 0,
                    difference: 0
                });
            }
            navRows.push({
                instrumentId: "",
                name: "Total",
                units: 0,
                navPerUnit: 0,
                shareOfNav: totalShare * 100,
                navValue: totalNav,
                accountingValue: accountingEquity,
                difference: accountingEquity - totalNav
            });
        }

        return {
            summaryRows,
            positionRows: sortBy(positionRows, "name"),
            bankAccountRows: sortBy(bankAccountRows, "name"),
            navRows,
            nav: activeNav
        };
    }, [run, runsData, journalEntriesData, portfolioData, navData, accountingCurrency]);

    const positionColumns = useMemo<ReactTableColumnDef<PositionRow>[]>(() => {
        const columnHelper = createReactTableColumnHelper<PositionRow>();
        // narrower than the other tables so that all columns of a fund fit
        const POSITION_SIZE = 112;
        const columns: ReactTableColumnDef<PositionRow>[] = [
            columnHelper.accessor("name", {
                header: "Instrument",
                cell: (cellProps) => <Link to={`/instruments/${cellProps.row.original.instrumentId}`}>{cellProps.getValue()}</Link>,
                size: 220
            }),
            numberColumn<PositionRow>("portfolioQuantity", "Portfolio qty", QUANTITY_DECIMALS, null, POSITION_SIZE)
        ];
        if (isFund) columns.push(numberColumn<PositionRow>("navQuantity", "NAV qty", QUANTITY_DECIMALS, null, POSITION_SIZE));
        columns.push(numberColumn<PositionRow>("accountingQuantity", "Accounting qty", QUANTITY_DECIMALS, null, POSITION_SIZE));
        columns.push(numberColumn<PositionRow>("quantityDifference", "Qty diff portfolio", QUANTITY_DECIMALS, 0, POSITION_SIZE));
        if (isFund) {
            columns.push(numberColumn<PositionRow>("navQuantityDifference", "Qty diff NAV", QUANTITY_DECIMALS, 0, POSITION_SIZE));
        }
        columns.push(numberColumn<PositionRow>("portfolioValue", "Portfolio value", accountingDecimals, null, POSITION_SIZE));
        if (isFund) columns.push(numberColumn<PositionRow>("navValue", "NAV value", accountingDecimals, null, POSITION_SIZE));
        columns.push(numberColumn<PositionRow>("accountingValue", "Accounting value", accountingDecimals, null, POSITION_SIZE));
        columns.push(
            numberColumn<PositionRow>("valueDifference", "Value diff portfolio", accountingDecimals, SMALL_VALUE_DIFFERENCE, POSITION_SIZE)
        );
        if (isFund) {
            columns.push(
                numberColumn<PositionRow>("navValueDifference", "Value diff NAV", accountingDecimals, SMALL_VALUE_DIFFERENCE, POSITION_SIZE)
            );
        }
        return columns;
    }, [isFund, accountingDecimals]);

    const bankAccountColumns = useMemo<ReactTableColumnDef<BankAccountRow>[]>(() => {
        const columnHelper = createReactTableColumnHelper<BankAccountRow>();
        return [
            columnHelper.accessor("name", {
                header: "Cash account",
                cell: (cellProps) => <Link to={`/instruments/${cellProps.row.original.instrumentId}`}>{cellProps.getValue()}</Link>,
                size: 250
            }),
            columnHelper.accessor("currency", { header: "Currency", size: 75 }),
            numberColumn<BankAccountRow>("portfolioLocalValue", "Balance (local)", 2),
            numberColumn<BankAccountRow>("accountingLocalValue", "Accounting (local)", 2),
            numberColumn<BankAccountRow>("localValueDifference", "Difference (local)", 2, SMALL_VALUE_DIFFERENCE),
            numberColumn<BankAccountRow>("portfolioValue", "Balance", accountingDecimals),
            numberColumn<BankAccountRow>("accountingValue", "Accounting value", accountingDecimals),
            numberColumn<BankAccountRow>("valueDifference", "Difference", accountingDecimals, SMALL_VALUE_DIFFERENCE)
        ];
    }, [accountingDecimals]);

    const summaryColumns = useMemo<ReactTableColumnDef<SummaryRow>[]>(() => {
        const columnHelper = createReactTableColumnHelper<SummaryRow>();
        return [
            columnHelper.accessor("name", { header: "", size: 200, enableSorting: false, enableColumnFilter: false }),
            numberColumn<SummaryRow>("portfolioValue", "Portfolio value", accountingDecimals),
            numberColumn<SummaryRow>("accountingValue", "Accounting value", accountingDecimals),
            numberColumn<SummaryRow>("difference", "Difference", accountingDecimals, SMALL_VALUE_DIFFERENCE)
        ];
    }, [accountingDecimals]);

    const navColumns = useMemo<ReactTableColumnDef<NavRow>[]>(() => {
        const columnHelper = createReactTableColumnHelper<NavRow>();
        return [
            columnHelper.accessor("name", {
                header: "Share class",
                cell: (cellProps) =>
                    cellProps.row.original.instrumentId ? (
                        <Link to={`/instruments/${cellProps.row.original.instrumentId}`}>{cellProps.getValue()}</Link>
                    ) : (
                        <b>{cellProps.getValue()}</b>
                    ),
                size: 250,
                enableSorting: false
            }),
            numberColumn<NavRow>("units", "Units", UNIT_DECIMALS),
            numberColumn<NavRow>("navPerUnit", "NAV per unit", UNIT_DECIMALS),
            numberColumn<NavRow>("shareOfNav", "%", 2),
            numberColumn<NavRow>("navValue", "NAV", accountingDecimals),
            numberColumn<NavRow>("accountingValue", "Accounting equity", accountingDecimals),
            numberColumn<NavRow>("difference", "Difference", accountingDecimals, SMALL_VALUE_DIFFERENCE)
        ];
    }, [accountingDecimals]);

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (!run) return <p>Accounting run not found</p>;

    // one workbook with all checks, from the Excel icon of any table
    const exportReconciliation = () => {
        const sheets: unknown[][] = [summaryRows, positionRows, bankAccountRows];
        const sheetNames = ["Summary", "Positions", "Bank accounts"];
        if (isFund) {
            sheets.push(navRows);
            sheetNames.push("NAV");
        }
        exportToXlsx(
            sheets,
            `${run.client?.name || run.clientId} ${run.accountingPeriod} run ${run.number} reconciliation.xlsx`,
            sheetNames
        );
    };

    const loading = fetchingRuns || fetchingJournalEntries || fetchingPortfolio || fetchingNav;
    const queryError = runsError || journalEntriesError || portfolioError || navError;

    return (
        <div>
            <div className="flex gap-4">
                <Link to={`/accountingruns2/${clientId}/${id}`}>Accounting run {run.number}</Link>
                <Link to={`/accountingruns2/${clientId}/${id}/statements`}>Balance sheet and income statement</Link>
            </div>
            <h4>Reconciliation, {run.client?.name}</h4>
            <p>
                Period {run.accountingPeriod}, end date {run.endDate}, accounting run {run.number} ({run.status}), accounting currency{" "}
                {accountingCurrency}. The journal entries of the runs of the period up to and including the run against the portfolio
                {isFund ? " and the NAV" : ""} at the end date. A value difference of at most 1 is yellow, a larger difference and any
                quantity difference red.
            </p>
            {loading ? <p>Loading</p> : null}
            {queryError ? <p>error: {JSON.stringify(queryError, null, 2)}</p> : null}
            <div className="mb-4">
                <h5>Summary</h5>
                <p>
                    The equity of the portfolio and its parts against the accounting, equity is credit and shown with the opposite sign. The
                    result of the portfolio is the equity less the create redeems, the dividends paid and the ingoing equity at{" "}
                    {previousEndDate}.
                </p>
                <ReactTable columns={summaryColumns} data={summaryRows} onExport={exportReconciliation} />
            </div>
            {isFund ? (
                <div className="mb-4">
                    <h5>NAV</h5>
                    {nav ? (
                        <React.Fragment>
                            <p>Active NAV of {nav.date} against the equity, the result of the year included.</p>
                            <ReactTable columns={navColumns} data={navRows} onExport={exportReconciliation} />
                        </React.Fragment>
                    ) : (
                        <p>No active NAV on or before {run.endDate}.</p>
                    )}
                </div>
            ) : null}
            <div className="mb-4">
                <h5>Bank accounts</h5>
                <p>The cash account balances by value date against the settled accounting position, forward cash left out.</p>
                <ReactTable columns={bankAccountColumns} data={bankAccountRows} onExport={exportReconciliation} />
            </div>
            <div className="mb-4">
                <h5>Positions</h5>
                <p>
                    The positions by trade date against the accounting position, initial cost, value change, accrued interest and forward
                    cash. Margin accounts are left out.
                </p>
                <ReactTable columns={positionColumns} data={positionRows} onExport={exportReconciliation} />
            </div>
        </div>
    );
}
